import React from 'react';
import { Text } from '@fluentui/react';
import { 
    VerticalBarChart, 
    DonutChart, 
    LineChart,
    HorizontalBarChart,
    GroupedVerticalBarChart
} from '@fluentui/react-charting';
import { FiTrendingUp, FiTrendingDown, FiMinus } from 'react-icons/fi';
import './FluentUIWidgets.css';

/**
 * Widget de métrique avec Fluent UI
 */
export const FluentMetricWidget = ({ title, value, subtitle, trend, color = '#0078D4', icon: Icon }) => {
    const getTrendIcon = () => {
        if (trend > 0) return <FiTrendingUp className="fluent-trend-up" />;
        if (trend < 0) return <FiTrendingDown className="fluent-trend-down" />;
        return <FiMinus className="fluent-trend-neutral" />;
    };

    return (
        <div className="fluent-metric-widget" style={{ borderLeft: `4px solid ${color}` }}>
            <div className="fluent-metric-header">
                <Text variant="medium" className="fluent-metric-title">{title}</Text>
                {Icon && (
                    <div className="fluent-metric-icon" style={{ backgroundColor: `${color}15`, color }}>
                        <Icon size={20} />
                    </div>
                )}
            </div>
            
            <div className="fluent-metric-body">
                <Text variant="xxLarge" className="fluent-metric-value" style={{ color }}>
                    {value}
                </Text>
                
                {subtitle && (
                    <Text variant="small" className="fluent-metric-subtitle">{subtitle}</Text>
                )}
                
                {trend !== undefined && (
                    <div className="fluent-metric-trend">
                        {getTrendIcon()}
                        <Text variant="small">{Math.abs(trend)}%</Text>
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * Widget de graphique en barres verticales avec Fluent UI
 */
export const FluentBarChartWidget = ({ title, data, color = '#0078D4', height = 300 }) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
        return (
            <div className="fluent-chart-widget">
                <Text variant="large" className="fluent-chart-title">{title || 'Bar Chart'}</Text>
                <div className="fluent-chart-empty">
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>📊</div>
                    <div>No data available</div>
                </div>
            </div>
        );
    }

    // Transformer les données pour Fluent UI VerticalBarChart
    const chartData = data.map(item => ({
        x: item.label,
        y: item.value,
        color: color
    }));

    return (
        <div className="fluent-chart-widget">
            <Text variant="large" className="fluent-chart-title">{title}</Text>
            <div className="fluent-chart-container">
                <VerticalBarChart
                    data={chartData}
                    height={height}
                    width="100%"
                    yAxisTickCount={5}
                    barWidth={32}
                    colors={[color]}
                    hideLegend={true}
                    enableReflow={true}
                />
            </div>
        </div>
    );
};

/**
 * Widget de graphique en barres horizontales avec Fluent UI
 */
export const FluentHorizontalBarChartWidget = ({ title, data, color = '#0078D4', height = 300 }) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
        return (
            <div className="fluent-chart-widget">
                <Text variant="large" className="fluent-chart-title">{title || 'Horizontal Bar Chart'}</Text>
                <div className="fluent-chart-empty">
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>📊</div>
                    <div>No data available</div>
                </div>
            </div>
        );
    }

    // Transformer les données pour Fluent UI HorizontalBarChart
    const chartData = data.map(item => ({
        chartTitle: item.label,
        chartData: [
            {
                legend: item.label,
                data: item.value,
                color: item.color || color
            }
        ]
    }));

    return (
        <div className="fluent-chart-widget">
            <Text variant="large" className="fluent-chart-title">{title}</Text>
            <div className="fluent-chart-container">
                <HorizontalBarChart
                    data={chartData}
                    height={height}
                    width="100%"
                    hideLegend={true}
                    hideTooltip={false}
                    enableReflow={true}
                />
            </div>
        </div>
    );
};

/**
 * Widget de graphique en donut avec Fluent UI
 */
export const FluentDonutChartWidget = ({ title, data, height = 300 }) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
        return (
            <div className="fluent-chart-widget">
                <Text variant="large" className="fluent-chart-title">{title || 'Donut Chart'}</Text>
                <div className="fluent-chart-empty">
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>🍩</div>
                    <div>No data available</div>
                </div>
            </div>
        );
    }

    // Transformer les données pour Fluent UI DonutChart
    const chartData = {
        chartTitle: title,
        chartData: data.map(item => ({
            legend: item.label,
            data: item.value,
            color: item.color
        }))
    };

    return (
        <div className="fluent-chart-widget">
            <Text variant="large" className="fluent-chart-title">{title}</Text>
            <div className="fluent-chart-container">
                <DonutChart
                    data={chartData}
                    height={height}
                    width="100%"
                    innerRadius={55}
                    hideLabels={false}
                    showLabelsInPercent={true}
                    enableReflow={true}
                />
            </div>
        </div>
    );
};

/**
 * Widget de graphique en ligne avec Fluent UI
 */
export const FluentLineChartWidget = ({ title, data, color = '#0078D4', height = 300 }) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
        return (
            <div className="fluent-chart-widget">
                <Text variant="large" className="fluent-chart-title">{title || 'Line Chart'}</Text>
                <div className="fluent-chart-empty">
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>📈</div>
                    <div>No data available</div>
                </div>
            </div>
        );
    }

    // Transformer les données pour Fluent UI LineChart
    const chartData = {
        chartTitle: title,
        lineChartData: [
            {
                legend: title,
                data: data.map(item => ({
                    x: item.label,
                    y: item.value
                })),
                color: color
            }
        ]
    };

    return (
        <div className="fluent-chart-widget">
            <Text variant="large" className="fluent-chart-title">{title}</Text>
            <div className="fluent-chart-container">
                <LineChart
                    data={chartData}
                    height={height}
                    width="100%"
                    hideLegend={true}
                    enableReflow={true}
                    yAxisTickCount={5}
                />
            </div>
        </div>
    );
};

/**
 * Widget de graphique en barres groupées avec Fluent UI
 */
export const FluentGroupedBarChartWidget = ({ title, data, height = 300 }) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
        return (
            <div className="fluent-chart-widget">
                <Text variant="large" className="fluent-chart-title">{title || 'Grouped Bar Chart'}</Text>
                <div className="fluent-chart-empty">
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>📊</div>
                    <div>No data available</div>
                </div>
            </div>
        );
    }

    return (
        <div className="fluent-chart-widget">
            <Text variant="large" className="fluent-chart-title">{title}</Text>
            <div className="fluent-chart-container">
                <GroupedVerticalBarChart
                    data={data}
                    height={height}
                    width="100%"
                    enableReflow={true}
                />
            </div>
        </div>
    );
};
